import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { launchFireworks } from 'shared/utils/fireworks';
import styles from './publicform.module.scss';
import { KoButton, KoImage, KoLoader, KoProgressCircle, KoText, KoTitle, KoToast } from 'packages';
import { Layout, Form, Input } from 'antd';
import KoIcon from '@kwapio/icons';

// Example widgets
const CustomerType = () => <Input placeholder="Customer Type" />;
const CustomerStatus = () => <Input placeholder="Customer Status" />;

const meta = {
  columns: 4,
  formItemLayout: null,
  initialValues: {},
  colon: true,
  fields: [
    {
      key: 'companyName',
      label: 'Company Name',
      placeholder: 'Ex: qubernet, inc.',
      colSpan: 2,
      rules: [{ required: true, message: 'Company name is required' }]
    },
    {
      key: 'type',
      label: 'Customer Type',
      placeholder: 'Enter GST',
      colSpan: 2,
      widget: CustomerType
    },
    {
      key: 'status',
      label: 'Customer Status',
      placeholder: 'Enter GST',
      colSpan: 2,
      widget: CustomerStatus
    },
    {
      key: 'url',
      label: 'Website Url',
      placeholder: 'Ex: https://qubernet.com',
      colSpan: 2,
      rules: [
        {
          pattern: /^https?:\/\/([\w\d]+\.)+\w{2,}(\/.+)?$/,
          message: 'Enter valid URL'
        }
      ]
    },
    { key: 'GST', label: 'GST Number', placeholder: 'Enter GST', colSpan: 2 },
    { key: 'PAN', label: 'PAN/EIN Number', placeholder: 'Enter PAN/EIN', colSpan: 2 },
    { key: 'TAN', label: 'TAN Number', placeholder: 'Enter TAN', colSpan: 2 },
    { key: 'UAN', label: 'UAN Number', placeholder: 'Enter UAN', colSpan: 2 }
  ]
};

export default function CardForm({ formData, requestId }) {
  const [isLoading] = useState(false);
  const [form] = Form.useForm();
  const [page, setPage] = useState(0);

  const { Header } = Layout;

  const pageSize = 4;
  const totalPages = Math.ceil(meta.fields.length / pageSize);
  const currentFields = meta.fields.slice(page * pageSize, page * pageSize + pageSize);

  const handleNext = async() => {
    try {
      await form.validateFields(currentFields.map(f => f.key));
      setPage(p => p + 1);
    } catch (err) {
      KoToast.error(`Page ${page + 1} has mandatory fields not filled`);
    }
  };

  const handlePrev = () => setPage(p => p - 1);

  const handleSubmit = async() => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue();
      console.log('Form Submitted:', values);
      KoToast.success('Form Submitted Successfully');
      launchFireworks();
    } catch {
      KoToast.error('Please fill all required fields before submitting.');
    }
  };

  const percent = ((page + 1) / totalPages) * 100;

  return (
    <div className={styles.wrapper}>
      {isLoading && <KoLoader />}
      <Header className={styles.shallowHeader}></Header>
      <Header className={styles.globalHeader}>
        <div className={styles.docHeader}>
          <div className={styles.docHeaderLeft}>
            <div className={styles.iconWrapper}>
              <KoImage src={'https://littleelly.com/wp-content/uploads/2025/06/logo.IYvJkGUK.png'} width={30} />
            </div>
            <KoTitle className={styles.docTitle} level={4} text={formData?.name || 'Customer Form'} />
          </div>
          <div className={styles.docHeaderRight}>
            <div className={styles.userWrapper} >
              {requestId && <KoText ellipsis={true} className={styles.name} text={`${requestId}`} />}
            </div>
            <KoProgressCircle
              type="circle"
              percent={Math.round(percent)}
              strokeWidth={6}
              size={38}
              format={() => `${page + 1}/${totalPages}`}
            />
          </div>
        </div>
      </Header>
      <div className={styles.cardFormWrapper}>
        <div className={styles.formWrapper}>
          <Form
            form={form}
            layout="vertical"
            initialValues={meta.initialValues}
            colon={meta.colon}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={page}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.4, ease: 'easeInOut' }}
                className={styles.fieldGrid}
                style={{ gridTemplateColumns: `repeat(${pageSize > 1 ? 2 : 1}, 1fr)` }}
              >
                {currentFields.map(field => {
                  const Widget = field.widget || Input;
                  return (
                    <div
                      key={field.key}
                      className={`${styles.formCard} ${styles[`colSpan ${field.colSpan}`]}`}
                    >
                      <Form.Item
                        name={field.key}
                        label={field.label}
                        rules={field.rules || []}
                      >
                        <Widget placeholder={field.placeholder} />
                      </Form.Item>
                    </div>
                  );
                })}
              </motion.div>
            </AnimatePresence>
          </Form>
          <div className={styles.footer}>
            <KoButton
              disabled={page === 0}
              iconBtn={true}
              icon={<KoIcon width={14} height={14} name="ArrowLeftOutlined" />}
              onClick={handlePrev}
            />
            {page < totalPages - 1 ? (
              <KoButton
                iconBtn={true}
                icon={<KoIcon width={14} height={14} name="ArrowRightOutlined" />}
                onClick={handleNext}
              />
            ) : (
              <KoButton onClick={handleSubmit} label="Submit" type="primary" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
